import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Switch,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { MessagingConfig } from '../../../models/Concession'
import { useMessagingConfig } from '../../../hooks/useMessagingConfig'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        maxWidth: 560,
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1.5),
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
}))

interface SettingsTabProps {
    readOnly?: boolean
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
    readOnly = false,
}) => {
    const classes = useStyles()
    const { config, isLoading, updateConfig } = useMessagingConfig()

    const [form, setForm] = useState<MessagingConfig>({
        email_enabled: true,
        sms_enabled: true,
        portal_message_enabled: true,
        reply_to_email: '',
        sender_name: '',
    })
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        if (config) {
            setForm(config)
            setDirty(false)
        }
    }, [config])

    const update = (patch: Partial<MessagingConfig>) => {
        setForm((prev) => ({ ...prev, ...patch }))
        setDirty(true)
    }

    const handleSave = () => {
        updateConfig.mutate(form, {
            onSuccess: () => setDirty(false),
        })
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.section}>
                <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                >
                    Communication Channels
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginBottom: 8, display: 'block' }}
                >
                    Enable or disable communication channels for renewal
                    sequences.
                </Typography>

                <Box className={classes.row}>
                    <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Email
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Send renewal messages via email
                        </Typography>
                    </Box>
                    <Switch
                        checked={form.email_enabled}
                        onChange={(e) =>
                            update({ email_enabled: e.target.checked })
                        }
                        color="primary"
                        disabled={readOnly}
                    />
                </Box>

                <Box className={classes.row}>
                    <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            SMS
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Send renewal messages via text message
                        </Typography>
                    </Box>
                    <Switch
                        checked={form.sms_enabled}
                        onChange={(e) =>
                            update({ sms_enabled: e.target.checked })
                        }
                        color="primary"
                        disabled={readOnly}
                    />
                </Box>

                <Box className={classes.row}>
                    <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            Portal Messages
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Send renewal messages via tenant portal
                        </Typography>
                    </Box>
                    <Switch
                        checked={form.portal_message_enabled}
                        onChange={(e) =>
                            update({
                                portal_message_enabled: e.target.checked,
                            })
                        }
                        color="primary"
                        disabled={readOnly}
                    />
                </Box>
            </Box>

            <Box className={classes.section}>
                <Typography
                    variant="subtitle2"
                    className={classes.sectionTitle}
                >
                    Email Settings
                </Typography>

                <Box className={classes.fieldGroup}>
                    <TextField
                        label="Reply-to email"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={form.reply_to_email}
                        onChange={(e) =>
                            update({ reply_to_email: e.target.value })
                        }
                        placeholder="renewals@yourproperty.com"
                        disabled={readOnly}
                    />
                    <TextField
                        label="Sender name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={form.sender_name}
                        onChange={(e) =>
                            update({ sender_name: e.target.value })
                        }
                        placeholder="Leasing Team"
                        disabled={readOnly}
                    />
                </Box>
            </Box>

            {!readOnly && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!dirty || updateConfig.isLoading}
                    style={{
                        textTransform: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        boxShadow: 'none',
                    }}
                >
                    {updateConfig.isLoading ? 'Saving...' : 'Save Settings'}
                </Button>
            )}
        </Box>
    )
}
